import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface UserReputation {
  userId: string
  reputation: number
  approvedSuggestions: number
  isMaxRep: boolean
  createdAt: number
  lastUpdated: number
}

export interface UserVote {
  tagId: string
  voteType: "upvote" | "downvote"
}

interface UserReputationState {
  reputations: UserReputation[]
  userVotes: Record<string, Record<string, "upvote" | "downvote">> // userId -> tagId -> vote type

  // Actions
  addReputation: (userId: string, points: number) => void
  incrementApprovedSuggestions: (userId: string) => void
  getUserReputation: (userId: string) => UserReputation | undefined
  isMaxRepUser: (userId: string) => boolean
  recordVote: (userId: string, tagId: string, voteType: "upvote" | "downvote") => boolean
  removeVote: (userId: string, tagId: string) => void
  getVoteType: (userId: string, tagId: string) => "upvote" | "downvote" | null
  hasVoted: (userId: string, tagId: string) => boolean
}

export const useUserReputationStore = create<UserReputationState>()(
  persist(
    (set, get) => ({
      reputations: [],
      userVotes: {},

      addReputation: (userId, points) => {
        set((state) => {
          const existing = state.reputations.find((r) => r.userId === userId)
          let updated: UserReputation[]

          if (existing) {
            const newReputation = existing.reputation + points
            const isMaxRep = newReputation >= 100
            updated = state.reputations.map((r) =>
              r.userId === userId
                ? {
                    ...r,
                    reputation: newReputation,
                    isMaxRep,
                    lastUpdated: Date.now(),
                  }
                : r,
            )
          } else {
            const newRep: UserReputation = {
              userId,
              reputation: points,
              approvedSuggestions: 0,
              isMaxRep: points >= 100,
              createdAt: Date.now(),
              lastUpdated: Date.now(),
            }
            updated = [...state.reputations, newRep]
          }

          return { reputations: updated }
        })
      },

      incrementApprovedSuggestions: (userId) => {
        set((state) => ({
          reputations: state.reputations.map((r) =>
            r.userId === userId
              ? {
                  ...r,
                  approvedSuggestions: r.approvedSuggestions + 1,
                  reputation: r.reputation + 5,
                  lastUpdated: Date.now(),
                }
              : r,
          ),
        }))
      },

      getUserReputation: (userId) => {
        return get().reputations.find((r) => r.userId === userId)
      },

      isMaxRepUser: (userId) => {
        const rep = get().reputations.find((r) => r.userId === userId)
        return rep?.isMaxRep ?? false
      },

      recordVote: (userId, tagId, voteType) => {
        set((state) => {
          const userVotes = state.userVotes[userId] || {}

          return {
            userVotes: {
              ...state.userVotes,
              [userId]: {
                ...userVotes,
                [tagId]: voteType,
              },
            },
          }
        })
        return true
      },

      removeVote: (userId, tagId) => {
        set((state) => {
          const userVotes = state.userVotes[userId]
          if (!userVotes) return state

          const { [tagId]: _, ...remainingVotes } = userVotes

          return {
            userVotes: {
              ...state.userVotes,
              [userId]: remainingVotes,
            },
          }
        })
      },

      getVoteType: (userId, tagId) => {
        const userVotes = get().userVotes[userId]
        return userVotes?.[tagId] || null
      },

      hasVoted: (userId, tagId) => {
        const userVotes = get().userVotes[userId]
        return userVotes?.[tagId] !== undefined
      },
    }),
    {
      name: "user-reputation-storage",
    },
  ),
)
